import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Order } from 'models/order';
import { LabClient } from 'models/client-directory';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import { getDeliveryAdvanceWorkingDate, WorkingDayBeforeCounter } from 'utils/internal-delivery-advance-working-date';

type DeliveryDateState = {
    date: Date | null;
    daysAdvance: number | undefined;
};

type UseDeliveryDatesResult = {
    internal: DeliveryDateState;
    external: DeliveryDateState;
    setInternalDate: (date: Date | null) => void;
    setExternalDate: (date: Date | null) => void;
    loading: boolean;
};

const initDateFromOrder = (
    deliveryDate: number | Date | undefined,
    advanceDate: Date | null | undefined,
): DeliveryDateState | null => {
    if (!advanceDate || !deliveryDate) return null;
    const clinicDate = new Date(deliveryDate);
    const resolvedDate = advanceDate instanceof Date ? advanceDate : new Date(advanceDate);
    const workingDayCount = WorkingDayBeforeCounter(clinicDate, resolvedDate);
    return { date: resolvedDate, daysAdvance: workingDayCount };
};

const initDateFromClient = (
    deliveryDate: number | Date | undefined,
    daysAdvance: number | undefined,
): DeliveryDateState | null => {
    if (!daysAdvance || !deliveryDate) return null;
    const clinicDate = new Date(deliveryDate);
    const advanceDate = getDeliveryAdvanceWorkingDate(clinicDate, daysAdvance);
    if (!advanceDate) return null;
    return { date: advanceDate, daysAdvance };
};

export const useDeliveryDates = (order: Order | undefined, onError: (error: Error) => void): UseDeliveryDatesResult => {
    const [internal, setInternal] = useState<DeliveryDateState>({ date: null, daysAdvance: undefined });
    const [external, setExternal] = useState<DeliveryDateState>({ date: null, daysAdvance: undefined });

    const [getLabClientById, { loading }] = useLazyQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ labClientById: client }: { labClientById: LabClient }) => {
            const deliveryDate = order?.deliveryDetails?.date;
            const internalResult = initDateFromClient(deliveryDate, client?.internalDeliveryDaysAdvance as number);
            if (internalResult) setInternal(internalResult);

            const externalResult = initDateFromClient(deliveryDate, client?.externalDeliveryDaysAdvance as number);
            if (externalResult) setExternal(externalResult);
        },
        onError,
    });

    useEffect(() => {
        if (!order) return;
        const deliveryDate = order.deliveryDetails?.date;

        const internalFromOrder = initDateFromOrder(deliveryDate, order.deliveryDetails?.internalDeliveryDate);
        if (internalFromOrder) {
            setInternal(internalFromOrder);
        }

        const externalFromOrder = initDateFromOrder(deliveryDate, order.deliveryDetails?.externalDeliveryDate);
        if (externalFromOrder) {
            setExternal(externalFromOrder);
        }

        const needsClientFallback = !internalFromOrder || !externalFromOrder;
        if (needsClientFallback && order.deliveryDetails?.destination?._id) {
            getLabClientById({ variables: { customerId: order.deliveryDetails.destination._id } });
        }
    }, [order]);

    return {
        internal,
        external,
        setInternalDate: (date) => setInternal((prev) => ({ ...prev, date })),
        setExternalDate: (date) => setExternal((prev) => ({ ...prev, date })),
        loading,
    };
};
